/**
 * MASTER — RECIPE / «Рецепт», Stage 1: HEADER + INGREDIENTS.
 *
 * This is the first structured slice of the professional Recipe Master.
 * It intentionally covers only:
 *   – recipe header (eyebrow, title, subtitle/lead, yield, mould/size);
 *   – one optional main image (uses existing shared image controls);
 *   – structured ingredient groups with reorderable rows.
 *
 * Stage 2 (technology, temperatures, critical points, storage) will be
 * added later — not in this sprint. Do not extend without a new brief.
 *
 * Storage:
 *   All new data lives inside `block.content.recipe`. Legacy top-level
 *   `content.eyebrow / headline / lead / body / ...` are preserved
 *   untouched so nothing is lost from prior authoring sessions.
 *
 * Reused primitives:
 *   – ImageSlot, MediaLibraryPicker (from HeroMaster).
 *   – CropModal (shared).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, ChefHat, BookOpen, ClipboardPaste, PenLine,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import EditorialPilotDialog from "@/components/editorial/EditorialPilotDialog";
import {
  ImageSlot,
  MediaLibraryPicker,
} from "@/components/editorial/HeroMaster";
import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle, hasExplicitEditorialCrop } from "@/lib/editorialReaderLayout.mjs";
import { getRecipe, listIngredients, listRecipes } from "@/lib/masterOrderApi";
import { parseRecipe } from "@/pages/masterOrder/recipeRecognition.mjs";
import {
  collectedRecipeSections,
  editorialRecipeFromLibrary,
  editorialRecipeFromRecognition,
  hasEditorialRecipeContent,
  hasRecipeNoteContent,
  hasRecipeStorageContent,
  recipeRecognitionWarning,
} from "@/lib/editorialSimplePilot.mjs";

// -------------------- content shape helpers --------------------

const genId = (p = "id") =>
  `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

const recipeSectionKey = (value) => String(value || "")
  .trim()
  .replace(/[«»"']/g, "")
  .replace(/\s+/g, " ")
  .toLocaleLowerCase("ru");

// Read the structured recipe payload from a block, falling back to legacy
// top-level content fields the first time the block is opened after the
// switch-over. Never mutates input: returns a normalised local copy.
function readRecipe(rawContent) {
  const c = rawContent || {};
  const r = (c.recipe && typeof c.recipe === "object") ? c.recipe : {};
  const emptyRow = () => ({ id: genId("r"), name: "", qty: "", unit: "", note: "" });
  const emptyGroup = () => ({ id: genId("g"), title: "", rows: [emptyRow()] });

  const groups = Array.isArray(r.ingredient_groups) && r.ingredient_groups.length
    ? r.ingredient_groups.map((g) => ({
        id: g.id || genId("g"),
        title: g.title || "",
        rows: (Array.isArray(g.rows) ? g.rows : []).map((row) => ({
          id: row.id || genId("r"),
          name: row.name || "",
          qty: row.qty || "",
          unit: row.unit || "",
          note: row.note || "",
        })),
      }))
    : [emptyGroup()];

  const steps = Array.isArray(r.technology_steps)
    ? r.technology_steps.map((s) => ({
        id:          s.id || genId("s"),
        title:       s.title || "",
        instruction: s.instruction || "",
        explicit_step: Boolean(s.explicit_step),
        time:        s.time || "",
        temperature: s.temperature || "",
        image: {
          media_id: s.image?.media_id ?? null,
          url:      s.image?.url      ?? null,
          focal_x:  s.image?.focal_x  ?? 0.5,
          focal_y:  s.image?.focal_y  ?? 0.5,
          zoom:     s.image?.zoom     ?? 1,
          alt:      s.image?.alt      ?? "",
        },
      }))
    : [];

  const readNoteList = (arr, prefix) => (Array.isArray(arr) ? arr : [])
    .filter(hasRecipeNoteContent)
    .map((n) => ({
    id:    n.id || genId(prefix),
    title: n.title || "",
    text:  n.text || "",
  }));
  const critical_points   = readNoteList(r.critical_points,   "cp");
  const professional_tips = readNoteList(r.professional_tips, "pt");

  const storage_items = (Array.isArray(r.storage_items) ? r.storage_items : [])
    .filter(hasRecipeStorageContent)
    .map((s) => ({
    id:        s.id || genId("st"),
    condition: s.condition || "",
    duration:  s.duration  || "",
    note:      s.note      || "",
  }));

  return {
    // header
    eyebrow:  r.eyebrow  ?? c.eyebrow  ?? "",
    title:    r.title    ?? c.headline ?? "",
    subtitle: r.subtitle ?? c.lead     ?? "",
    yield:    r.yield    ?? "",
    mould:    r.mould    ?? "",
    // hero image (single, optional)
    hero: {
      media_id: r.hero?.media_id ?? null,
      url:      r.hero?.url      ?? null,
      focal_x:  r.hero?.focal_x  ?? 0.5,
      focal_y:  r.hero?.focal_y  ?? 0.5,
      zoom:     r.hero?.zoom     ?? 1,
      alt:      r.hero?.alt      ?? "",
    },
    ingredient_groups: groups,
    technology_steps: steps,
    critical_points,
    professional_tips,
    storage_items,
    original_text: r.original_text ?? "",
    unresolved_lines: Array.isArray(r.unresolved_lines) ? r.unresolved_lines : [],
  };
}

// -------------------- Editor --------------------

// Shared editor panel for a simple ordered list of notes (id + title + text).
// Used by both «Критические точки» and «Профессиональные советы».
function NoteListPanel({
  title, addLabel, testidBase, titlePlaceholder, textPlaceholder, items, controls,
}) {
  if (items.length === 0) {
    return (
      <button type="button" onClick={controls.add}
        className="xl:col-span-2 w-full rounded-xl border border-dashed border-espresso/20 bg-cream/45 px-4 py-2.5 text-left text-sm text-espresso hover:bg-cream/70"
        data-testid={`${testidBase}-empty-add`}>
        + {addLabel}
      </button>
    );
  }
  return (
    <div className="xl:col-span-2 rounded-2xl bg-white border hairline p-4 space-y-3"
         data-testid={`${testidBase}-editor`}>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{title}</div>
        <button type="button" onClick={controls.add}
          className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
          data-testid={`${testidBase}-add`}>
          <Plus className="w-3.5 h-3.5" /> {addLabel}
        </button>
      </div>

      <div className="space-y-3">
          {items.map((n, i) => (
            <div key={n.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                 data-testid={`${testidBase}-item-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={n.title}
                       onChange={(e) => controls.setField(i, { title: e.target.value })}
                       placeholder={titlePlaceholder}
                       className="font-serif"
                       data-testid={`${testidBase}-title-${i}`} />
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => controls.move(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`${testidBase}-up-${i}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.move(i, +1)} disabled={i === items.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`${testidBase}-down-${i}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.remove(i)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить" data-testid={`${testidBase}-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <textarea rows={3} value={n.text}
                        onChange={(e) => controls.setField(i, { text: e.target.value })}
                        className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                        placeholder={textPlaceholder}
                        data-testid={`${testidBase}-text-${i}`} />
            </div>
          ))}
      </div>
    </div>
  );
}

// Editor panel for «Хранение и срок годности».
// Each row is a condition (long text), a compact duration, and an optional
// free-form note. Stable IDs and order are preserved by the parent state.
function StorageListPanel({ items, controls }) {
  if (items.length === 0) {
    return (
      <button type="button" onClick={controls.add}
        className="xl:col-span-2 w-full rounded-xl border border-dashed border-espresso/20 bg-cream/45 px-4 py-2.5 text-left text-sm text-espresso hover:bg-cream/70"
        data-testid="recipe-storage-empty-add">
        + Добавить информацию о хранении
      </button>
    );
  }
  return (
    <div className="xl:col-span-2 rounded-2xl bg-white border hairline p-4 space-y-3"
         data-testid="recipe-storage-editor">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Хранение и срок годности</div>
        <button type="button" onClick={controls.add}
          className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
          data-testid="recipe-storage-add">
          <Plus className="w-3.5 h-3.5" /> Добавить условие хранения
        </button>
      </div>

      <div className="space-y-3">
          {items.map((s, i) => (
            <div key={s.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                 data-testid={`recipe-storage-item-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,3fr)_minmax(0,1fr)_auto] gap-2 items-center">
                <Input value={s.condition}
                       onChange={(e) => controls.setField(i, { condition: e.target.value })}
                       placeholder="Условие · например «При комнатной температуре»"
                       className="font-serif"
                       data-testid={`recipe-storage-condition-${i}`} />
                <Input value={s.duration}
                       onChange={(e) => controls.setField(i, { duration: e.target.value })}
                       placeholder="Срок · например «до 2 суток»"
                       className="font-serif"
                       data-testid={`recipe-storage-duration-${i}`} />
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => controls.move(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`recipe-storage-up-${i}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.move(i, +1)} disabled={i === items.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`recipe-storage-down-${i}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.remove(i)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить" data-testid={`recipe-storage-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <Input value={s.note}
                     onChange={(e) => controls.setField(i, { note: e.target.value })}
                     placeholder="Примечание · необязательно"
                     data-testid={`recipe-storage-note-${i}`} />
            </div>
          ))}
      </div>
    </div>
  );
}

function RecipeLibraryChoice({ onUse }) {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([listRecipes(), listIngredients()])
      .then(([recipeRows, ingredientRows]) => {
        if (!active) return;
        setRecipes((recipeRows || []).filter((item) => item.is_active !== false));
        setIngredients(ingredientRows || []);
      })
      .catch(() => active && setError("Не удалось загрузить «Мои рецептуры»."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const choose = async (recipe) => {
    setBusyId(recipe.id);
    setError("");
    try {
      const fullRecipe = await getRecipe(recipe.id);
      onUse(editorialRecipeFromLibrary(fullRecipe, ingredients));
    } catch (_) {
      setError("Не удалось открыть рецептуру. Попробуйте ещё раз.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="recipe-library-choice">
      <p className="text-sm text-espresso/65">Выберите основу. Исходная рецептура останется без изменений.</p>
      {loading && <p className="mt-3 text-sm text-espresso/60">Загружаем рецептуры…</p>}
      {!loading && recipes.length === 0 && !error && (
        <p className="mt-3 text-sm text-espresso/60">В «Моих рецептурах» пока нет сохранённых рецептов.</p>
      )}
      {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {recipes.map((recipe) => (
          <button type="button" key={recipe.id} onClick={() => choose(recipe)} disabled={busyId === recipe.id}
            className="rounded-xl border border-espresso/15 bg-cream/45 p-3 text-left hover:bg-cream/75 disabled:opacity-60"
            data-testid={`recipe-library-use-${recipe.id}`}>
            <div className="font-serif text-base text-espresso">{recipe.name}</div>
            <div className="mt-1 text-[11px] text-espresso/60">
              {busyId === recipe.id ? "Открываем…" : "Использовать как основу"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}



export function RecipeEditor({
  block,
  onPatch,
  mode = "content",
  detailedOpen = false,
  onRequestDetailed,
  onReturnToCollected,
  onRequestPreview,
}) {
  const r = useMemo(() => readRecipe(block.content), [block.content]);
  const [entryMode, setEntryMode] = useState(null);
  const [sourceText, setSourceText] = useState("");
  const [recognitionError, setRecognitionError] = useState("");
  // Patch only the structured recipe payload — legacy top-level content
  // fields are preserved verbatim.
  const patch = (delta) => onPatch({
    content: { ...(block.content || {}), recipe: { ...r, ...delta } },
  });

  const applyStructuredRecipe = (structured, message) => {
    patch({
      ...structured,
      hero: r.hero,
      critical_points: r.critical_points,
      professional_tips: r.professional_tips,
      storage_items: r.storage_items,
    });
    setEntryMode(null);
    onReturnToCollected?.();
    setRecognitionError("");
    toast.success(message);
  };

  const recognizeRecipe = () => {
    const recognized = parseRecipe(sourceText);
    const structured = editorialRecipeFromRecognition(recognized);
    if (!hasEditorialRecipeContent(structured)) {
      setRecognitionError("Не удалось найти название, ингредиенты или технологию. Проверьте текст и попробуйте ещё раз.");
      return;
    }
    applyStructuredRecipe(structured, "Рецепт собран. Проверьте результат.");
  };

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  // Per-step media pickers — null | step index. Kept separate from the hero
  // pickers so opening one never closes/mutates the other.
  const [stepLibraryFor, setStepLibraryFor] = useState(null);
  const [stepCropFor, setStepCropFor] = useState(null);
  const [stepMediaOpen, setStepMediaOpen] = useState({});
  const hasContent = hasEditorialRecipeContent(r);
  const collected = collectedRecipeSections(r);

  // -- hero image --
  const setHero = (next) => patch({ hero: { ...r.hero, ...next } });
  const acceptFromLibrary = (m) => {
    setHero({ media_id: m.id, url: m.file_url || m.url });
    setLibraryOpen(false);
  };
  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    setHero({ focal_x, focal_y, zoom });
    setCropOpen(false);
  };

  // -- ingredient groups --
  const setGroups = (groups) => patch({ ingredient_groups: groups });
  const addGroup = () => setGroups([
    ...r.ingredient_groups,
    { id: genId("g"), title: "", rows: [{ id: genId("r"), name: "", qty: "", unit: "", note: "" }] },
  ]);
  const removeGroup = (gi) => {
    const next = [...r.ingredient_groups];
    next.splice(gi, 1);
    setGroups(next.length ? next : [{ id: genId("g"), title: "", rows: [{ id: genId("r"), name: "", qty: "", unit: "", note: "" }] }]);
  };
  const moveGroup = (gi, dir) => {
    const next = [...r.ingredient_groups];
    const j = gi + dir;
    if (j < 0 || j >= next.length) return;
    [next[gi], next[j]] = [next[j], next[gi]];
    setGroups(next);
  };
  const patchGroup = (gi, delta) => {
    const next = [...r.ingredient_groups];
    const previousTitle = next[gi]?.title || "";
    next[gi] = { ...next[gi], ...delta };
    if (Object.prototype.hasOwnProperty.call(delta, "title")) {
      const previousKey = recipeSectionKey(previousTitle);
      const technology_steps = r.technology_steps.map((step) => (
        !step.explicit_step && previousKey && recipeSectionKey(step.title) === previousKey
          ? { ...step, title: delta.title }
          : step
      ));
      patch({ ingredient_groups: next, technology_steps });
      return;
    }
    setGroups(next);
  };
  const addRow = (gi) => {
    const g = r.ingredient_groups[gi];
    patchGroup(gi, {
      rows: [...g.rows, { id: genId("r"), name: "", qty: "", unit: "", note: "" }],
    });
  };
  const removeRow = (gi, ri) => {
    const g = r.ingredient_groups[gi];
    const rows = [...g.rows]; rows.splice(ri, 1);
    patchGroup(gi, { rows: rows.length ? rows : [{ id: genId("r"), name: "", qty: "", unit: "", note: "" }] });
  };
  const moveRow = (gi, ri, dir) => {
    const g = r.ingredient_groups[gi];
    const rows = [...g.rows];
    const j = ri + dir;
    if (j < 0 || j >= rows.length) return;
    [rows[ri], rows[j]] = [rows[j], rows[ri]];
    patchGroup(gi, { rows });
  };
  const patchRow = (gi, ri, delta) => {
    const g = r.ingredient_groups[gi];
    const rows = [...g.rows];
    rows[ri] = { ...rows[ri], ...delta };
    patchGroup(gi, { rows });
  };

  // -- technology steps --
  const setSteps = (steps) => patch({ technology_steps: steps });
  const emptyStep = () => ({
    id: genId("s"), title: "", instruction: "", explicit_step: true, time: "", temperature: "",
    image: { media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1, alt: "" },
  });
  const addStep = () => setSteps([...r.technology_steps, emptyStep()]);
  const removeStep = (si) => {
    const next = [...r.technology_steps]; next.splice(si, 1); setSteps(next);
  };
  const moveStep = (si, dir) => {
    const next = [...r.technology_steps];
    const j = si + dir;
    if (j < 0 || j >= next.length) return;
    [next[si], next[j]] = [next[j], next[si]];
    setSteps(next);
  };
  const patchStep = (si, delta) => {
    const next = [...r.technology_steps];
    next[si] = { ...next[si], ...delta };
    setSteps(next);
  };
  const patchStepImage = (si, delta) => {
    const s = r.technology_steps[si];
    patchStep(si, { image: { ...s.image, ...delta } });
  };

  const componentTechnologyIndexes = (groupIndex) => {
    const group = r.ingredient_groups[groupIndex];
    const groupKey = recipeSectionKey(group?.title);
    return r.technology_steps
      .map((step, index) => ({ step, index }))
      .filter(({ step }) => {
        if (step.explicit_step) return false;
        if (groupKey) return recipeSectionKey(step.title) === groupKey;
        return r.ingredient_groups.length === 1 && !recipeSectionKey(step.title);
      })
      .map(({ index }) => index);
  };
  const isComponentTechnology = (step, index) => r.ingredient_groups.some((_, gi) => (
    componentTechnologyIndexes(gi).includes(index)
  ));
  const addComponentTechnology = (groupIndex) => {
    const group = r.ingredient_groups[groupIndex];
    setSteps([...r.technology_steps, {
      ...emptyStep(),
      title: group?.title || "",
      explicit_step: false,
    }]);
  };
  const sequentialSteps = r.technology_steps
    .map((step, index) => ({ step, index }))
    .filter(({ step, index }) => step.explicit_step || !isComponentTechnology(step, index));
  const acceptStepFromLibrary = (m) => {
    if (stepLibraryFor == null) return;
    patchStepImage(stepLibraryFor, { media_id: m.id, url: m.file_url || m.url });
    setStepLibraryFor(null);
  };
  const saveStepCrop = ({ focal_x, focal_y, zoom }) => {
    if (stepCropFor == null) return;
    patchStepImage(stepCropFor, { focal_x, focal_y, zoom });
    setStepCropFor(null);
  };

  // -- generic note-list controls (critical_points / professional_tips) --
  // Returns a stable set of CRUD helpers bound to `key` (either
  // "critical_points" or "professional_tips"). All operations preserve
  // stable IDs and current order.
  const makeNoteControls = (key, idPrefix) => ({
    add: () => patch({ [key]: [...r[key], { id: genId(idPrefix), title: "", text: "" }] }),
    remove: (i) => {
      const next = [...r[key]]; next.splice(i, 1); patch({ [key]: next });
    },
    move: (i, dir) => {
      const next = [...r[key]];
      const j = i + dir;
      if (j < 0 || j >= next.length) return;
      [next[i], next[j]] = [next[j], next[i]];
      patch({ [key]: next });
    },
    setField: (i, delta) => {
      const next = [...r[key]];
      next[i] = { ...next[i], ...delta };
      patch({ [key]: next });
    },
  });
  const cp = makeNoteControls("critical_points", "cp");
  const pt = makeNoteControls("professional_tips", "pt");

  // -- storage items (condition / duration / optional note) --
  const st = {
    add: () => patch({
      storage_items: [
        ...r.storage_items,
        { id: genId("st"), condition: "", duration: "", note: "" },
      ],
    }),
    remove: (i) => {
      const next = [...r.storage_items]; next.splice(i, 1);
      patch({ storage_items: next });
    },
    move: (i, dir) => {
      const next = [...r.storage_items];
      const j = i + dir;
      if (j < 0 || j >= next.length) return;
      [next[i], next[j]] = [next[j], next[i]];
      patch({ storage_items: next });
    },
    setField: (i, delta) => {
      const next = [...r.storage_items];
      next[i] = { ...next[i], ...delta };
      patch({ storage_items: next });
    },
  };

  return (
    <div className="space-y-3" data-testid="recipe-editor">
      {mode === "content" && !detailedOpen && (
      <div className="rounded-2xl border hairline bg-cream/55 p-4" data-testid="recipe-simple-entry">
        <div>
          <div className="font-serif text-xl text-espresso">Как добавить рецепт</div>
          <p className="mt-1 text-[12px] text-espresso/65">Выберите готовую рецептуру, вставьте рецепт целиком или заполните его вручную.</p>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <button type="button" onClick={() => setEntryMode(entryMode === "library" ? null : "library")}
            className="rounded-xl border border-espresso/15 bg-white p-3 text-left hover:bg-cream/60"
            data-testid="recipe-entry-library">
            <BookOpen className="h-4 w-4 text-espresso" />
            <div className="mt-2 text-sm font-medium text-espresso">Выбрать из моих рецептур</div>
            <p className="mt-1 text-[11px] leading-snug text-espresso/60">Взять сохранённую рецептуру как редактируемую основу.</p>
          </button>
          <button type="button" onClick={() => setEntryMode(entryMode === "paste" ? null : "paste")}
            className="rounded-xl border border-espresso/15 bg-white p-3 text-left hover:bg-cream/60"
            data-testid="recipe-entry-paste">
            <ClipboardPaste className="h-4 w-4 text-espresso" />
            <div className="mt-2 text-sm font-medium text-espresso">Вставить рецепт</div>
            <p className="mt-1 text-[11px] leading-snug text-espresso/60">Система разложит текст на ингредиенты, компоненты и технологию.</p>
          </button>
          <button type="button" onClick={onRequestDetailed}
            className="rounded-xl border border-espresso/15 bg-white p-3 text-left hover:bg-cream/60"
            data-testid="recipe-entry-manual">
            <PenLine className="h-4 w-4 text-espresso" />
            <div className="mt-2 text-sm font-medium text-espresso">Заполнить вручную</div>
            <p className="mt-1 text-[11px] leading-snug text-espresso/60">Открыть все редактируемые поля без распознавания.</p>
          </button>
        </div>

        {r.unresolved_lines.length > 0 && hasContent && (
          <p className="mt-3 rounded-xl border border-amber-700/15 bg-amber-50 px-3 py-2 text-[12px] text-amber-900"
            data-testid="recipe-recognition-warning">
            {recipeRecognitionWarning(r.unresolved_lines.length)}
          </p>
        )}
      </div>
      )}

      {mode === "content" && (!hasContent || detailedOpen) && (
        <div className="rounded-2xl border border-espresso/10 bg-white p-4" data-testid="recipe-photo-entry">
          <div className="mb-3">
            <div className="font-serif text-lg text-espresso">Фото рецепта</div>
            <p className="mt-1 text-[12px] text-espresso/60">Фото добавляется независимо от способа ввода рецепта.</p>
          </div>
          <div className="max-w-[260px]">
            <ImageSlot label="Основное фото" url={r.hero.url}
              focalX={r.hero.focal_x} focalY={r.hero.focal_y} zoom={r.hero.zoom}
              onFile={(m) => setHero({ media_id: m.id, url: m.file_url })}
              onClear={() => setHero({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
              onOpenLibrary={() => setLibraryOpen(true)} onOpenCrop={() => setCropOpen(true)}
              clearLabel="Удалить" testid="recipe-primary-photo" />
          </div>
        </div>
      )}

      {!detailedOpen && entryMode === "paste" && (
        <EditorialPilotDialog title="Вставить рецепт" onClose={() => setEntryMode(null)}
          testid="recipe-paste-dialog">
          <label className="block text-sm text-espresso/75" htmlFor="recipe-paste-text">
            Вставьте рецепт целиком
          </label>
          <textarea id="recipe-paste-text" rows={15} value={sourceText}
            onChange={(event) => setSourceText(event.target.value)}
            className="mt-2 w-full rounded-xl border border-espresso/20 bg-white p-4 text-sm leading-relaxed outline-none focus:border-espresso/45"
            placeholder="Название, выход, компоненты, ингредиенты и технология приготовления"
            data-testid="recipe-paste-text" />
          {recognitionError && <p className="mt-2 text-[12px] text-red-700" role="alert">{recognitionError}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setEntryMode(null)}
              className="rounded-xl border border-espresso/20 px-4 py-2 text-sm">Отмена</button>
            <button type="button" onClick={recognizeRecipe}
              className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream"
              data-testid="recipe-recognize-submit">Распознать рецепт</button>
          </div>
        </EditorialPilotDialog>
      )}
      {!detailedOpen && entryMode === "library" && (
        <EditorialPilotDialog title="Мои рецептуры" onClose={() => setEntryMode(null)}
          testid="recipe-library-dialog">
          <RecipeLibraryChoice
            onUse={(structured) => applyStructuredRecipe(structured, "Рецептура добавлена как редактируемая основа.")} />
        </EditorialPilotDialog>
      )}

      {mode === "content" && hasContent && !detailedOpen && (
        <div className="rounded-2xl border border-espresso/10 bg-white p-5" data-testid="recipe-collected-result">
          <div className="text-[10px] uppercase tracking-[0.18em] text-espresso/50">Собранный рецепт</div>
          <h3 className="mt-2 font-serif text-3xl text-espresso">{r.title || "Рецепт без названия"}</h3>
          {r.subtitle && <p className="mt-2 font-serif text-lg italic text-espresso/70">{r.subtitle}</p>}
          {(r.yield || r.mould) && <p className="mt-2 text-xs uppercase tracking-wider text-espresso/55">
            {[r.yield && `Выход: ${r.yield}`, r.mould && `Форма: ${r.mould}`].filter(Boolean).join(" · ")}
          </p>}
          <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="space-y-4">
            {collected.sections.map((section, groupIndex) => (
              <section key={section.id} className="rounded-xl bg-cream/40 p-4" data-testid={`recipe-collected-group-${groupIndex}`}>
                <h4 className="font-serif text-xl text-espresso">{section.title}</h4>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-espresso/50">Ингредиенты</div>
                <ul className="mt-2 divide-y divide-espresso/10 text-sm">
                  {section.rows.filter((row) => row.name || row.qty || row.unit).map((row) => (
                    <li key={row.id} className="flex justify-between gap-3 py-1.5">
                      <span>{row.name}</span><span className="shrink-0 text-espresso/65">{[row.qty, row.unit].filter(Boolean).join(" ")}</span>
                    </li>
                  ))}
                </ul>
                {section.technology.length > 0 && (
                  <div className="mt-4 border-t border-espresso/10 pt-3" data-testid={`recipe-collected-group-technology-${groupIndex}`}>
                    <div className="text-[10px] uppercase tracking-widest text-espresso/50">Технология</div>
                    <div className="mt-2 space-y-3 text-sm leading-relaxed text-espresso/80">
                      {section.technology.map((step, index) => (
                        <div key={step.id} className={step.explicit_step ? "grid grid-cols-[24px_minmax(0,1fr)] gap-3" : ""}>
                          {step.explicit_step && <span className="font-mono text-xs text-espresso/45">{String(index + 1).padStart(2, "0")}</span>}
                          <div>{step.title && section.technology.length > 1 && <strong className="font-medium">{step.title}</strong>}
                            {step.instruction && <p className={step.title && section.technology.length > 1 ? "mt-1 whitespace-pre-line" : "whitespace-pre-line"}>{step.instruction}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            ))}
            {collected.unmatched.length > 0 && (
              <section className="rounded-xl bg-cream/40 p-4" data-testid="recipe-collected-technology">
                <h4 className="font-serif text-xl text-espresso">Технология</h4>
                <div className="mt-3 space-y-3 text-sm leading-relaxed text-espresso/80">
                  {collected.unmatched.map((step) => (
                    <div key={step.id}>
                      {step.title && <strong className="font-medium">{step.title}</strong>}
                      {step.instruction && <p className={step.title ? "mt-1 whitespace-pre-line" : "whitespace-pre-line"}>{step.instruction}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}
            {r.unresolved_lines.length > 0 && (
              <section className="rounded-xl border border-amber-700/20 bg-amber-50 p-4" data-testid="recipe-collected-uncertain">
                <div className="text-[10px] uppercase tracking-widest text-amber-900/70">Проверьте распознавание</div>
                <div className="mt-2 space-y-2">
                  {r.unresolved_lines.map((line, index) => (
                    <div key={`${line}-${index}`} className="rounded-lg border border-amber-700/20 bg-white px-3 py-2 text-sm text-amber-950"
                      data-testid={`recipe-collected-uncertain-row-${index}`}>
                      {line}
                    </div>
                  ))}
                </div>
              </section>
            )}
            </div>
            <aside className="rounded-xl border border-espresso/10 bg-white p-3" data-testid="recipe-collected-photo">
              <ImageSlot label="Основное фото" url={r.hero.url}
                focalX={r.hero.focal_x} focalY={r.hero.focal_y} zoom={r.hero.zoom}
                onFile={(m) => setHero({ media_id: m.id, url: m.file_url })}
                onClear={() => setHero({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
                onOpenLibrary={() => setLibraryOpen(true)} onOpenCrop={() => setCropOpen(true)}
                clearLabel="Удалить" testid="recipe-collected-primary-photo" />
            </aside>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" onClick={onRequestDetailed}
              className="rounded-xl border border-espresso/20 px-4 py-2 text-sm text-espresso"
              data-testid="recipe-detailed-open">Редактировать подробно</button>
            <button type="button" onClick={onRequestPreview}
              className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream"
              data-testid="recipe-preview-open">Предпросмотр</button>
          </div>
        </div>
      )}

      {mode === "design" && (
        <div className="rounded-2xl border border-espresso/10 bg-white p-5" data-testid="recipe-design-layer">
          <div className="font-serif text-xl text-espresso">Оформление рецепта</div>
          <p className="mt-1 text-[12px] text-espresso/60">Композиция MASTER сохранена. Здесь настраивается главное изображение публикации.</p>
          <div className="mt-4 max-w-xl">
            <ImageSlot label="Основное фото" url={r.hero.url}
              focalX={r.hero.focal_x} focalY={r.hero.focal_y} zoom={r.hero.zoom}
              onFile={(m) => setHero({ media_id: m.id, url: m.file_url })}
              onClear={() => setHero({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
              onOpenLibrary={() => setLibraryOpen(true)} onOpenCrop={() => setCropOpen(true)}
              clearLabel="Удалить" testid="recipe-design-photo" />
            <Input className="mt-2" placeholder="Описание изображения"
              value={r.hero.alt} onChange={(e) => setHero({ alt: e.target.value })}
              data-testid="recipe-design-photo-alt" />
          </div>
        </div>
      )}

      {mode === "content" && detailedOpen && (
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
           data-testid="recipe-structured-result">
      {/* LEFT — header + hero image */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Шапка рецепта</div>
        </div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Надзаголовок · необязательно</label>
          <Input value={r.eyebrow} onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="БИСКВИТЫ · 04" data-testid="recipe-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Название рецепта</label>
          <Input value={r.title} onChange={(e) => patch({ title: e.target.value })}
                 className="font-serif text-lg" data-testid="recipe-field-title" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подзаголовок · необязательно</label>
          <textarea rows={2} value={r.subtitle}
                    onChange={(e) => patch({ subtitle: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic"
                    data-testid="recipe-field-subtitle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] opacity-60 mb-1">Выход · необязательно</label>
            <Input value={r.yield} onChange={(e) => patch({ yield: e.target.value })}
                   placeholder="12 порций" data-testid="recipe-field-yield" />
          </div>
          <div>
            <label className="block text-[11px] opacity-60 mb-1">Форма / размер · необязательно</label>
            <Input value={r.mould} onChange={(e) => patch({ mould: e.target.value })}
                   placeholder="форма Ø 18 см" data-testid="recipe-field-mould" />
          </div>
        </div>

      </div>

      {/* RIGHT — ingredient groups */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-3">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Ингредиенты</div>
          <button type="button" onClick={addGroup}
            className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
            data-testid="recipe-add-group">
            <Plus className="w-3.5 h-3.5" /> Добавить группу
          </button>
        </div>

        <div className="space-y-3">
          {r.ingredient_groups.map((g, gi) => {
            const technologyIndexes = componentTechnologyIndexes(gi);
            return (
            <div key={g.id} className="rounded-lg border hairline bg-cream/40 p-3"
                 data-testid={`recipe-group-${gi}`}>
              <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:items-center">
                <Input value={g.title}
                       onChange={(e) => patchGroup(gi, { title: e.target.value })}
                       placeholder="Название группы · например «Бисквит»"
                       className="font-serif"
                       data-testid={`recipe-group-title-${gi}`} />
                <div className="flex items-center gap-1 shrink-0 self-end sm:self-auto">
                  <button type="button" onClick={() => moveGroup(gi, -1)} disabled={gi === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`recipe-group-up-${gi}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => moveGroup(gi, +1)} disabled={gi === r.ingredient_groups.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`recipe-group-down-${gi}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => removeGroup(gi)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить группу" data-testid={`recipe-group-remove-${gi}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {g.rows.map((row, ri) => (
                  <div key={row.id}
                       className="grid grid-cols-2 gap-2 items-center sm:grid-cols-[minmax(0,3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)_auto]"
                       data-testid={`recipe-row-${gi}-${ri}`}>
                    <Input value={row.name}
                           onChange={(e) => patchRow(gi, ri, { name: e.target.value })}
                           placeholder="Ингредиент"
                           className="col-span-2 sm:col-span-1"
                           data-testid={`recipe-row-name-${gi}-${ri}`} />
                    <Input value={row.qty}
                           onChange={(e) => patchRow(gi, ri, { qty: e.target.value })}
                           placeholder="Кол-во"
                           data-testid={`recipe-row-qty-${gi}-${ri}`} />
                    <Input value={row.unit}
                           onChange={(e) => patchRow(gi, ri, { unit: e.target.value })}
                           placeholder="Ед."
                           data-testid={`recipe-row-unit-${gi}-${ri}`} />
                    <Input value={row.note}
                           onChange={(e) => patchRow(gi, ri, { note: e.target.value })}
                           placeholder="Примечание · необязательно"
                           className="col-span-2 sm:col-span-1"
                           data-testid={`recipe-row-note-${gi}-${ri}`} />
                    <div className="col-span-2 flex items-center justify-end gap-0.5 sm:col-span-1 sm:justify-start">
                      <button type="button" onClick={() => moveRow(gi, ri, -1)} disabled={ri === 0}
                        className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                        title="Выше" data-testid={`recipe-row-up-${gi}-${ri}`}>
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveRow(gi, ri, +1)} disabled={ri === g.rows.length - 1}
                        className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                        title="Ниже" data-testid={`recipe-row-down-${gi}-${ri}`}>
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeRow(gi, ri)}
                        className="p-1 opacity-60 hover:opacity-100 text-red-600"
                        title="Удалить строку" data-testid={`recipe-row-remove-${gi}-${ri}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={() => addRow(gi)}
                className="mt-2 text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                data-testid={`recipe-add-row-${gi}`}>
                <Plus className="w-3.5 h-3.5" /> Добавить ингредиент
              </button>

              <div className="mt-4 border-t border-espresso/10 pt-3"
                data-testid={`recipe-group-technology-${gi}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-70">Технология компонента</div>
                    <p className="mt-1 text-[11px] text-espresso/55">Связный способ приготовления этого компонента.</p>
                  </div>
                  {technologyIndexes.length === 0 && (
                    <button type="button" onClick={() => addComponentTechnology(gi)}
                      className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                      data-testid={`recipe-add-group-technology-${gi}`}>
                      <Plus className="w-3.5 h-3.5" /> Добавить технологию
                    </button>
                  )}
                </div>
                {technologyIndexes.map((stepIndex) => {
                  const step = r.technology_steps[stepIndex];
                  return (
                    <div key={step.id} className="mt-3 space-y-2" data-testid={`recipe-group-technology-item-${gi}-${stepIndex}`}>
                      <textarea rows={4} value={step.instruction}
                        onChange={(e) => patchStep(stepIndex, { instruction: e.target.value, explicit_step: false })}
                        className="w-full rounded border px-2 py-1.5 text-sm font-serif leading-relaxed"
                        placeholder="Опишите приготовление компонента связным текстом"
                        data-testid={`recipe-group-technology-text-${gi}-${stepIndex}`} />
                      <div className="flex flex-wrap items-center gap-2">
                        <Input value={step.time}
                          onChange={(e) => patchStep(stepIndex, { time: e.target.value })}
                          placeholder="Время · необязательно" className="sm:max-w-[180px]" />
                        <Input value={step.temperature}
                          onChange={(e) => patchStep(stepIndex, { temperature: e.target.value })}
                          placeholder="Температура · необязательно" className="sm:max-w-[220px]" />
                        <button type="button" onClick={() => removeStep(stepIndex)}
                          className="text-xs text-red-700 sm:ml-auto"
                          data-testid={`recipe-remove-group-technology-${gi}-${stepIndex}`}>
                          Удалить технологию
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );})}
        </div>
      </div>

      {/* FULL-WIDTH — explicit sequential technology only */}
      <div className="xl:col-span-2 rounded-2xl bg-white border hairline p-4 space-y-3"
           data-testid="recipe-technology-editor">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Последовательные этапы</div>
            <p className="mt-1 text-[11px] text-espresso/55">Добавляйте только когда рецепт действительно требует отдельных шагов.</p>
          </div>
          <button type="button" onClick={addStep}
            className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
            data-testid="recipe-add-step">
            <Plus className="w-3.5 h-3.5" /> Добавить отдельный этап
          </button>
        </div>

        {sequentialSteps.length === 0 && (
          <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
            <div className="text-[12px] opacity-70 mb-2">
              Отдельных этапов нет. Технология компонентов редактируется рядом с ингредиентами.
            </div>
            <button type="button" onClick={addStep}
              className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-espresso text-cream"
              data-testid="recipe-empty-add-step">
              <Plus className="w-3.5 h-3.5" /> Добавить отдельный этап
            </button>
          </div>
        )}

        <div className="space-y-3">
          {sequentialSteps.map(({ step: s, index: si }, displayIndex) => (
            <div key={s.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-3"
                 data-testid={`recipe-step-${si}`}>
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase tracking-widest opacity-60 shrink-0 font-mono">
                  {String(displayIndex + 1).padStart(2, "0")}
                </div>
                <Input value={s.title}
                       onChange={(e) => patchStep(si, { title: e.target.value })}
                       placeholder="Название шага · например «Взбить желтки»"
                       className="font-serif"
                       data-testid={`recipe-step-title-${si}`} />
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => moveStep(si, -1)} disabled={si === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`recipe-step-up-${si}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => moveStep(si, +1)} disabled={si === r.technology_steps.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`recipe-step-down-${si}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => removeStep(si)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить шаг" data-testid={`recipe-step-remove-${si}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] opacity-60 mb-1">Инструкция</label>
                    <textarea rows={4} value={s.instruction}
                              onChange={(e) => patchStep(si, { instruction: e.target.value })}
                              className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                              data-testid={`recipe-step-instruction-${si}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] opacity-60 mb-1">Время · необязательно</label>
                      <Input value={s.time}
                             onChange={(e) => patchStep(si, { time: e.target.value })}
                             placeholder="15 мин"
                             data-testid={`recipe-step-time-${si}`} />
                    </div>
                    <div>
                      <label className="block text-[11px] opacity-60 mb-1">Температура · необязательно</label>
                      <Input value={s.temperature}
                             onChange={(e) => patchStep(si, { temperature: e.target.value })}
                             placeholder="170 °C"
                             data-testid={`recipe-step-temp-${si}`} />
                    </div>
                  </div>
                </div>
                {(s.image.url || stepMediaOpen[si]) ? (
                <div className="max-w-[280px] rounded-xl border border-espresso/10 bg-white/70 p-3"
                  data-testid={`recipe-step-media-panel-${si}`}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-espresso/60">Фото шага</span>
                    {!s.image.url && <button type="button"
                      onClick={() => setStepMediaOpen((current) => ({ ...current, [si]: false }))}
                      className="text-[11px] text-espresso/55 underline">Свернуть</button>}
                  </div>
                  <ImageSlot
                    label=""
                    url={s.image.url}
                    focalX={s.image.focal_x} focalY={s.image.focal_y} zoom={s.image.zoom}
                    onFile={(m) => patchStepImage(si, { media_id: m.id, url: m.file_url })}
                    onClear={() => patchStepImage(si, { media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
                    onOpenLibrary={() => setStepLibraryFor(si)}
                    onOpenCrop={() => setStepCropFor(si)}
                    testid={`recipe-step-image-${si}`} />
                  <div className="mt-2">
                    <Input placeholder="Описание изображения · необязательно"
                           value={s.image.alt}
                           onChange={(e) => patchStepImage(si, { alt: e.target.value })}
                           data-testid={`recipe-step-image-alt-${si}`} />
                  </div>
                </div>
                ) : (
                  <button type="button"
                    onClick={() => setStepMediaOpen((current) => ({ ...current, [si]: true }))}
                    className="rounded-xl border border-dashed border-espresso/20 px-3 py-2 text-left text-xs text-espresso/70"
                    data-testid={`recipe-step-add-photo-${si}`}>
                    + Добавить фото шага
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FULL-WIDTH — critical points + professional tips */}
      <NoteListPanel
        title="ВАЖНО"
        testidBase="recipe-critical"
        addLabel="Добавить важное замечание"
        titlePlaceholder="Например «Температура выпекания»"
        textPlaceholder="Опишите, что критично для получения нужного результата…"
        items={r.critical_points}
        controls={cp} />
      <NoteListPanel
        title="СОВЕТЫ АВТОРА"
        testidBase="recipe-tip"
        addLabel="Добавить совет автора"
        titlePlaceholder="Например «Мука обязательно должна быть просеяна»"
        textPlaceholder="Кратко и по существу — совет от шефа…"
        items={r.professional_tips}
        controls={pt} />
      <StorageListPanel items={r.storage_items} controls={st} />

      </div>
      )}
      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onPick={acceptFromLibrary} />
      <MediaLibraryPicker
        open={stepLibraryFor != null}
        onClose={() => setStepLibraryFor(null)}
        onPick={acceptStepFromLibrary} />
      <CropModal
        open={cropOpen}
        url={r.hero.url}
        focalX={r.hero.focal_x}
        focalY={r.hero.focal_y}
        zoom={r.hero.zoom}
        onClose={() => setCropOpen(false)}
        onSave={saveCrop} />
      <CropModal
        open={stepCropFor != null}
        url={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.url : null}
        focalX={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.focal_x ?? 0.5 : 0.5}
        focalY={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.focal_y ?? 0.5 : 0.5}
        zoom={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.zoom || 1 : 1}
        onClose={() => setStepCropFor(null)}
        onSave={saveStepCrop} />
    </div>
  );
}

// -------------------- Reader render --------------------

function HeroFrame({ hero, mode = "desktop" }) {
  if (!hero?.url) {
    return null;
  }
  const hasCrop = hasExplicitEditorialCrop(hero);
  const maxHeightClass = mode === "mobile" ? "max-h-[285px]" : "max-h-[460px]";
  return (
    <figure className={`flex w-full justify-center overflow-hidden ${hasCrop ? "aspect-[4/5] bg-neutral-100" : ""}`}
            data-testid={`recipe-primary-media-${mode}`}>
      <img
        src={resolveMediaUrl(hero.url)}
        alt={hero.alt || ""}
        className={hasCrop ? "h-full w-full" : `block h-auto w-auto max-w-full ${maxHeightClass}`}
        style={editorialImageStyle(hero)}
      />
    </figure>
  );
}

// One ingredient group rendered in editorial style.
// When the recipe has only one group, the group title is intentionally
// hidden in reader — the surrounding «Ингредиенты» heading already scopes
// the list. Only genuinely composite recipes with multiple groups surface
// per-group titles.
function GroupCard({ group, technology = [], hideTitle = false }) {
  const hasRows = (group.rows || []).some((row) => row.name || row.qty || row.unit || row.note);
  return (
    <section className="space-y-3" data-testid={`recipe-render-group-${group.id}`}>
      {!hideTitle && group.title && (
        <h3 className="border-b border-espresso/20 pb-2 font-serif text-[19px] font-normal leading-[1.25]"
             style={{ color: "#2A1D14" }}>
          {group.title}
        </h3>
      )}
      {hasRows ? (
        <div>
          <div className="mb-1 text-[9px] uppercase tracking-[0.24em]" style={{ color: "#8A6A46" }}>
            Ингредиенты
          </div>
          <ul className="divide-y divide-espresso/10">
            {group.rows.map((row) => {
              if (!row.name && !row.qty && !row.unit && !row.note) return null;
              return (
                <li key={row.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 py-2">
                  <div>
                    <div className="font-serif text-[14px] leading-[1.4]" style={{ color: "#2A1D14" }}>
                      {row.name}
                    </div>
                    {row.note && (
                      <div className="mt-0.5 text-[12px] italic opacity-70" style={{ color: "#5B4326" }}>
                        {row.note}
                      </div>
                    )}
                  </div>
                  <div className="whitespace-nowrap text-[12px] tabular-nums" style={{ color: "#3E2E20" }}>
                    {row.qty}{row.qty && row.unit ? "\u00A0" : ""}{row.unit}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {technology.length > 0 && (
        <div className="border-t border-espresso/15 pt-4" data-testid={`recipe-render-group-technology-${group.id}`}>
          <div className="mb-2 text-[9px] uppercase tracking-[0.24em]" style={{ color: "#8A6A46" }}>
            Технология
          </div>
          <div className="space-y-3">
            {technology.map((step) => (
              <div key={step.id}>
                {step.title && technology.length > 1 && (
                  <div className="font-serif text-[15px]" style={{ color: "#2A1D14" }}>{step.title}</div>
                )}
                {step.instruction && (
                  <p className="whitespace-pre-wrap font-serif text-[14px] leading-[1.75]"
                     style={{ color: "#3E2E20" }}>{step.instruction}</p>
                )}
                {(step.time || step.temperature) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {step.time && <StepParam label="Время" value={step.time} />}
                    {step.temperature && <StepParam label="Температура" value={step.temperature} />}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// Distribute groups into balanced columns preserving order.
function distributeGroups(groups, cols) {
  const buckets = Array.from({ length: cols }, () => []);
  const weights = new Array(cols).fill(0);
  groups.forEach((g) => {
    // approximate weight = title + rows count
    const w = 1 + (Array.isArray(g.rows) ? g.rows.length : 0);
    let target = 0;
    for (let i = 1; i < cols; i += 1) {
      if (weights[i] < weights[target]) target = i;
    }
    buckets[target].push(g);
    weights[target] += w;
  });
  return buckets;
}

// Compact chip for optional step parameters (time / temperature).
function StepParam({ label, value }) {
  return (
    <div className="inline-flex items-baseline gap-2 py-1 px-3 rounded-full bg-cream/70 border border-espresso/10">
      <span className="text-[9px] tracking-[0.24em] uppercase opacity-70" style={{ color: "#5B4326" }}>
        {label}
      </span>
      <span className="font-serif text-[12px] tabular-nums" style={{ color: "#2A1D14" }}>
        {value}
      </span>
    </div>
  );
}

// One step rendered in editorial style. Number badge sits alongside title;
// the optional image only renders when a url is present so no empty
// placeholder is ever reserved.
function StepCard({ step, index, mode }) {
  const isMobile = mode === "mobile";
  const hasImage = !!(step.image && step.image.url);
  const isExplicitStep = step.explicit_step !== false;
  const num = String(index + 1).padStart(2, "0");

  const NumberBadge = (
    <div className="font-serif tabular-nums leading-none"
         style={{
           color: "#A67B4A",
           fontSize: isMobile ? "30px" : "38px",
           letterSpacing: "-0.02em",
         }}
         data-testid={`recipe-step-number-${step.id}`}>
      {num}
    </div>
  );

  const Params = (step.time || step.temperature) ? (
    <div className="flex flex-wrap gap-2 mt-3">
      {step.time && <StepParam label="Время" value={step.time} />}
      {step.temperature && <StepParam label="Температура" value={step.temperature} />}
    </div>
  ) : null;

  const TextBlock = (
    <div>
      <div className="flex items-baseline gap-4">
        {isExplicitStep && NumberBadge}
        <div className="min-w-0">
          {isExplicitStep && (
            <div className="text-[9px] tracking-[0.28em] uppercase opacity-60" style={{ color: "#5B4326" }}>
              Шаг {num}
            </div>
          )}
          {step.title && (
            <h4 className="font-serif leading-[1.2] mt-1"
                style={{
                  fontSize: isMobile ? "clamp(18px, 4.5vw, 20px)" : "clamp(19px, 1.8vw, 22px)",
                  color: "#2A1D14",
                }}>
              {step.title}
            </h4>
          )}
        </div>
      </div>
      {step.instruction && (
        <p className="mt-3 max-w-[62ch] whitespace-pre-wrap font-serif text-[14px] leading-[1.75]"
           style={{ color: "#3E2E20" }}>
          {step.instruction}
        </p>
      )}
      {Params}
    </div>
  );

  if (isMobile) {
    // Mobile — everything stacked, image (if any) between params and instruction
    // read order: number + title → params → image → instruction. Simpler:
    // number+title, image, params, instruction.
    return (
      <div className="space-y-3" data-testid={`recipe-step-card-${step.id}`}>
        <div className="flex items-baseline gap-4">
          {isExplicitStep && NumberBadge}
          <div className="min-w-0">
            {isExplicitStep && (
              <div className="text-[9px] tracking-[0.28em] uppercase opacity-60" style={{ color: "#5B4326" }}>
                Шаг {num}
              </div>
            )}
            {step.title && (
              <h4 className="font-serif leading-[1.2] mt-1"
                  style={{ fontSize: "clamp(18px, 4.5vw, 20px)", color: "#2A1D14" }}>
                {step.title}
              </h4>
            )}
          </div>
        </div>
        {hasImage && (
          <div className="overflow-hidden bg-neutral-100 aspect-[4/5]"
               style={{ maxHeight: 420 }}>
            <img src={resolveMediaUrl(step.image.url)} alt={step.image.alt || ""}
                 className="h-full w-full"
                 style={editorialImageStyle(step.image)} />
          </div>
        )}
        {step.instruction && (
          <p className="whitespace-pre-wrap font-serif text-[14px] leading-[1.75]"
             style={{ color: "#3E2E20" }}>
            {step.instruction}
          </p>
        )}
        {(step.time || step.temperature) && (
          <div className="flex flex-wrap gap-2">
            {step.time && <StepParam label="Время" value={step.time} />}
            {step.temperature && <StepParam label="Температура" value={step.temperature} />}
          </div>
        )}
      </div>
    );
  }

  // Desktop — image (if any) on one side, text on the other. Alternate side
  // on even/odd steps for editorial rhythm; when no image, text runs full width.
  if (!hasImage) {
    return (
      <div data-testid={`recipe-step-card-${step.id}`}>
        {TextBlock}
      </div>
    );
  }
  const imageOnLeft = index % 2 === 0;
  const ImageCol = (
    <div className="overflow-hidden bg-neutral-100 aspect-[4/5]"
         style={{ maxHeight: 680 }}>
      <img src={resolveMediaUrl(step.image.url)} alt={step.image.alt || ""}
           className="h-full w-full"
           style={editorialImageStyle(step.image)} />
    </div>
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 lg:gap-12 items-start"
         data-testid={`recipe-step-card-${step.id}`}>
      {imageOnLeft ? <>{ImageCol}<div>{TextBlock}</div></> : <><div>{TextBlock}</div>{ImageCol}</>}
    </div>
  );
}

// Reader column for a note list — Critical Points or Professional Tips.
// Kept minimal on purpose: calm hierarchy, no card frame, hairline divider
// between items, subtle accent bar based on `kind`.
function NoteColumn({ title, items, kind }) {
  const accent = kind === "critical" ? "#B8654A" : "#8A6A46";
  return (
    <div data-testid={`recipe-notes-${kind}`}>
      <div className="text-[10px] tracking-[0.28em] uppercase mb-6 pb-2 border-b border-espresso/25"
           style={{ color: "#5B4326" }}>
        {title}
      </div>
      <ul className="divide-y divide-espresso/10">
        {items.map((n) => (
          <li key={n.id} className="py-4 first:pt-0" data-testid={`recipe-note-${n.id}`}>
            {n.title && (
              <div className="flex items-baseline gap-3">
                <span className="inline-block w-1 h-4 shrink-0 mt-1"
                      style={{ background: accent }} aria-hidden />
                <h4 className="font-serif text-[17px] font-normal leading-[1.3] md:text-[18px]"
                    style={{ color: "#2A1D14" }}>
                  {n.title}
                </h4>
              </div>
            )}
            {n.text && (
              <p className={`max-w-[62ch] whitespace-pre-wrap font-serif text-[14px] leading-[1.75] ${n.title ? "mt-2 pl-4" : ""}`}
                 style={{ color: "#3E2E20" }}>
                {n.text}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}



// Reader section for «Хранение и срок годности». One calm full-width panel,
// hairline row separators, no card frame. Condition sits on the left,
// duration is emphasised on the right; the optional note appears under
// the condition only when present so no blank space is reserved.
function StorageSection({ items, mode }) {
  const isMobile = mode === "mobile";
  return (
    <div className={isMobile ? "mt-10" : "mt-14"} data-testid="recipe-storage-section">
      <div className="text-[10px] tracking-[0.28em] uppercase mb-6 pb-2 border-b border-espresso/25"
           style={{ color: "#5B4326" }}>
        Хранение и срок годности
      </div>
      <ul className="divide-y divide-espresso/10">
        {items.map((s) => (
          <li key={s.id}
              className={`py-4 first:pt-0 ${isMobile
                ? "space-y-1"
                : "grid grid-cols-[minmax(0,7fr)_minmax(0,3fr)] gap-8 items-baseline"}`}
              data-testid={`recipe-storage-row-${s.id}`}>
            <div className="min-w-0">
              {s.condition && (
                <div className="font-serif text-[14px] leading-[1.45]" style={{ color: "#2A1D14" }}>
                  {s.condition}
                </div>
              )}
              {s.note && (
                <div className="mt-1 font-serif italic text-[13px] leading-[1.5] max-w-[62ch]"
                     style={{ color: "#5B4326" }}>
                  {s.note}
                </div>
              )}
            </div>
            {s.duration && (
              <div className={`font-serif tabular-nums whitespace-nowrap ${isMobile ? "text-[14px]" : "text-[16px] text-right"}`}
                   style={{ color: "#3E2E20" }}>
                {s.duration}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}



export function RecipeRender({ block, mode = "desktop" }) {
  const r = useMemo(() => readRecipe(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const Header = (
    <>
      {r.eyebrow && (
        <div className="text-[10px] tracking-[0.28em] uppercase mb-3"
             style={{ color: "#A67B4A" }}>
          {r.eyebrow}
        </div>
      )}
      {r.title && (
        <h2 className="min-w-0 break-words font-serif leading-[1.08] [overflow-wrap:anywhere]"
            style={{
              fontSize: isMobile ? "clamp(24px, 6vw, 28px)" : "clamp(30px, 2.8vw, 38px)",
              color: "#2A1D14",
            }}
            data-testid="recipe-title">
          {r.title}
        </h2>
      )}
      {r.subtitle && (
        <p className="mt-3 max-w-[58ch] font-serif text-[14px] italic leading-[1.6]"
           style={{ color: "#5B4326" }}>
          {r.subtitle}
        </p>
      )}
    </>
  );

  const YieldCard = (r.yield || r.mould) ? (
    <div className="mt-5 inline-flex flex-wrap items-center gap-x-7 gap-y-2 border-y border-espresso/15 py-2.5"
         data-testid="recipe-yield-card">
      {r.yield && (
        <div className="text-[12px]" style={{ color: "#3E2E20" }}>
          <span className="text-[10px] tracking-[0.2em] uppercase opacity-70 mr-2">Выход</span>
          <span className="font-serif">{r.yield}</span>
        </div>
      )}
      {r.mould && (
        <div className="text-[12px]" style={{ color: "#3E2E20" }}>
          <span className="text-[10px] tracking-[0.2em] uppercase opacity-70 mr-2">Форма</span>
          <span className="font-serif">{r.mould}</span>
        </div>
      )}
    </div>
  ) : null;

  const groups = (r.ingredient_groups || []).filter((g) =>
    g.title || (g.rows || []).some((row) => row.name || row.qty || row.unit || row.note));

  // Skip empty technology steps — a step is considered non-empty if any
  // author-facing field carries content.
  const steps = (r.technology_steps || []).filter((s) =>
    s.title || s.instruction || s.time || s.temperature || (s.image && s.image.url));

  const technologyForGroup = (group) => {
    const groupKey = recipeSectionKey(group?.title);
    return steps.filter((step) => {
      if (step.explicit_step) return false;
      const stepKey = recipeSectionKey(step.title);
      if (groupKey) return stepKey === groupKey;
      return groups.length === 1 && !stepKey;
    });
  };
  const groupsWithTechnology = groups.map((group) => ({
    ...group,
    __technology: technologyForGroup(group),
  }));
  const claimedTechnologyIds = new Set(groupsWithTechnology.flatMap((group) => (
    group.__technology.map((step) => step.id)
  )));
  const explicitSteps = steps.filter((step) => step.explicit_step);
  const unmatchedTechnology = steps.filter((step) => (
    !step.explicit_step && !claimedTechnologyIds.has(step.id)
  ));
  const hasImage = !!(r.hero && r.hero.url);

  const TechnologySection = explicitSteps.length ? (
    <div className={isMobile ? "mt-10" : "mt-14"} data-testid="recipe-technology-section">
      <div className="text-[10px] tracking-[0.28em] uppercase mb-6 pb-2 border-b border-espresso/25"
           style={{ color: "#5B4326" }}>
        Технология
      </div>
      <div className={isMobile ? "space-y-10" : "space-y-14"}>
        {explicitSteps.map((step, index) => (
          <StepCard key={step.id} step={step} index={index} mode={mode} />
        ))}
      </div>
    </div>
  ) : null;

  const UnmatchedTechnologySection = unmatchedTechnology.length ? (
    <div className={isMobile ? "mt-8" : "mt-10"} data-testid="recipe-unmatched-technology">
      <div className="mb-4 border-b border-espresso/20 pb-2 text-[10px] uppercase tracking-[0.28em]"
           style={{ color: "#5B4326" }}>Технология</div>
      <div className="space-y-4">
        {unmatchedTechnology.map((step) => (
          <div key={step.id} className="max-w-[62ch]">
            {step.title && <h4 className="font-serif text-[18px] text-espresso">{step.title}</h4>}
            {step.instruction && (
              <p className="mt-1 whitespace-pre-wrap font-serif text-[15px] leading-[1.7] text-espresso/85">
                {step.instruction}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  // Skip empty note items — a note counts only if it has a title or text.
  const criticals = (r.critical_points   || []).filter((n) => n.title || n.text);
  const tips      = (r.professional_tips || []).filter((n) => n.title || n.text);
  const hasCriticals = criticals.length > 0;
  const hasTips      = tips.length > 0;

  const storageItems = (r.storage_items || []).filter(
    (s) => s.condition || s.duration || s.note);
  const StorageSectionEl = storageItems.length
    ? <StorageSection items={storageItems} mode={mode} />
    : null;

  const NotesSection = (hasCriticals || hasTips) ? (
    <div className={isMobile ? "mt-10" : "mt-14"} data-testid="recipe-notes-section">
      {isMobile || hasImage ? (
        <div className="space-y-10">
          {hasCriticals && (
            <NoteColumn kind="critical" title="Важно" items={criticals} />
          )}
          {hasTips && (
            <NoteColumn kind="tip" title="Советы автора" items={tips} />
          )}
        </div>
      ) : (
        <div className={`grid gap-10 lg:gap-14 ${hasCriticals && hasTips ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
          {hasCriticals && (
            <NoteColumn kind="critical" title="Важно" items={criticals} />
          )}
          {hasTips && (
            <NoteColumn kind="tip" title="Советы автора" items={tips} />
          )}
        </div>
      )}
    </div>
  ) : null;

  // Desktop: balance groups into 1 or 2 columns depending on count.
  const desktopCols = groups.length >= 3 ? 2 : 1;
  const buckets = distributeGroups(groupsWithTechnology, desktopCols);

  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#EFE6D5" }}
             data-testid={`recipe-render-${block.id}-${mode}`}>
      <div className={`mx-auto px-7 py-10 sm:px-9 sm:py-12 ${isMobile ? "" : "max-w-[1080px]"}`}>
        {isMobile ? (
          <>
            {hasImage && (
              <div className="mb-8">
                <HeroFrame hero={r.hero} mode="mobile" />
              </div>
            )}
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.24em] uppercase"
                   style={{ color: "#8A6A46" }}>
                <ChefHat className="w-3.5 h-3.5" /> Рецепт
              </div>
              {Header}
            </div>
            {YieldCard && <div className="mb-8">{YieldCard}</div>}
            <div className="space-y-10">
              {groupsWithTechnology.map((g) => (
                <GroupCard key={g.id} group={g} technology={g.__technology} hideTitle={groups.length === 1} />
              ))}
            </div>
            {UnmatchedTechnologySection}
            {TechnologySection}
            {NotesSection}
            {StorageSectionEl}
          </>
        ) : (
          <>
            {hasImage ? (
              <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,42fr)_minmax(0,58fr)] lg:gap-14">
                <aside className="min-w-0">
                  <HeroFrame hero={r.hero} mode="desktop" />
                  {NotesSection}
                  {StorageSectionEl}
                </aside>
                <div className="min-w-0">
                  <div className="mb-9 border-b border-espresso/15 pb-8">
                    <div className="mb-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em]"
                         style={{ color: "#8A6A46" }}>
                      <ChefHat className="h-3.5 w-3.5" /> Рецепт
                    </div>
                    {Header}
                    {YieldCard}
                  </div>
                  <div className={`grid gap-x-10 gap-y-11 ${desktopCols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                    {buckets.map((col, ci) => (
                      <div key={ci} className="space-y-10">
                        {col.map((g) => (
                          <GroupCard key={g.id} group={g} technology={g.__technology} hideTitle={groups.length === 1} />
                        ))}
                      </div>
                    ))}
                  </div>
                  {UnmatchedTechnologySection}
                  {TechnologySection}
                </div>
              </div>
            ) : (
              <>
                <div className="mb-8 max-w-[760px]">
                  <div className="mb-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em]"
                       style={{ color: "#8A6A46" }}>
                    <ChefHat className="h-3.5 w-3.5" /> Рецепт
                  </div>
                  {Header}
                  {YieldCard}
                </div>
                <div className={`grid gap-x-10 gap-y-10 ${desktopCols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                  {buckets.map((col, ci) => (
                    <div key={ci} className="space-y-8">
                      {col.map((g) => (
                        <GroupCard key={g.id} group={g} technology={g.__technology} hideTitle={groups.length === 1} />
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
            {!hasImage && (
              <>
                {UnmatchedTechnologySection}
                {TechnologySection}
                {NotesSection}
                {StorageSectionEl}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
